/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Tomáš Burian (burian@cadwork.cz)
 *
 */

import RenderDocument from "App/render_document";
import View from "App/view/view";
import ViewControls from "App/view/controls/view_controls";
import {vec3} from "gl-matrix";
import Camera from "App/view/cameras/camera";
import {degToRad} from "App/math";
import CameraAnimator from "App/animations/camera_animator";
//@ts-ignore
import {EViewControls, EViewControlsFamily} from "js_interface/src/modules/view_controls/scene_view_controls_enums.ts";

export default
class FileAnimationControls extends ViewControls {

    private _freeLook: boolean = false;
    private _repeat: boolean = false;
    private _lastViewDir: vec3 = null;
    private moveFlagMap: any = {};

    constructor(aDocument: RenderDocument, aView: View) {
        super(aDocument, aView);

        this._family = EViewControlsFamily.FILE_ANIMATION;
        this._camera = new Camera(this._view);
        this._initCameraParams();

        this.goForward = 0;
        this.goBack = 1;
        this.goLeft = 2;
        this.goRight = 3;
        this.goUp = 4;
        this.goDown = 5;

        this.moveFlagMap = [];
        this.moveFlagMap[this.goForward] = false;
        this.moveFlagMap[this.goBack] = false;
        this.moveFlagMap[this.goLeft] = false;
        this.moveFlagMap[this.goRight] = false;
        this.moveFlagMap[this.goUp] = false;
        this.moveFlagMap[this.goDown] = false;

        this._viewMode.scheduleRedraw();
    }

    public getControlsType(): EViewControls {
        return EViewControls.FILE_ANIMATION;
    }

    public onMousePress(x: number, y: number, buttonState: number): boolean {
        if (this._pointerLockUsed) {
            if (buttonState & 1) { // left mb
                this.moveFlagMap[this.goUp] = true;
            }
            if (buttonState & 2) { // right mb
                this.moveFlagMap[this.goDown] = true;
            }
        }
        this._mouseState.set(0, 0, x, y, x, y, buttonState, true);

        return true;
    }

    public onMouseRelease(x: number, y: number, buttonState: number, prevButton: number): boolean {
        if (prevButton & 1) { // left mb
            this.moveFlagMap[this.goUp] = false;
        }
        if (prevButton & 2) { // right mb
            this.moveFlagMap[this.goDown] = false;
        }

        this._mouseState.set(0, 0, x, y, this._mouseState.pressX, this._mouseState.pressY, buttonState, false);

        return true;
    }

    public onDrag(x: number, y: number, delta: number, buttonState: number): boolean {
        let dx = x - this._mouseState.lastX;
        let dy = y - this._mouseState.lastY;

        this._mouseState.isMoving = true;

        if (buttonState & 1 || buttonState & 2) { // left and right
            this.rotateCameraTarget(this._view.horizontalPixelsToRad(dx), this._view.verticalPixelsToRad(dy));
        }

        this._mouseState.dx = dx;
        this._mouseState.dy = dy;

        this._mouseState.lastX = x;
        this._mouseState.lastY = y;

        this._view.viewpointChanged();

        return true;
    }

    public onWheel(x: number, y: number, delta: number, buttonState: number): boolean {
        return true;
    }

    public onMouseIn(x: number, y: number, buttonState: number): boolean {
        this._mouseState.set(0, 0, x, y, this._mouseState.pressX, this._mouseState.pressY, 0, false);
        return true;
    }

    public onMouseOut(x: number, y: number, buttonState: number): boolean {
        this._mouseState.set(0, 0, x, y, this._mouseState.pressX, this._mouseState.pressY, 0, false);
        return true;
    }

    public onDoubleClick(x: number, y: number): boolean {
        return false;
    }

    public onKeyPress(key: string) {
        switch (key) {
            // cdwk
            /*
             case  43: // + (incr. speed)
             let evt = document.createEvent("Event");
             evt.initEvent("mousewheel", true, true);
             evt.wheelDelta = 120; // the 120 is default wheel value used in all major browsers?
             this._view.getHTMLCanvas().dispatchEvent(evt);
             break;
             case  45: // - (decr. speed)
             let evt = document.createEvent("Event");
             evt.initEvent("mousewheel", true, true);
             evt.wheelDelta = -120; // the -120 is default wheel value used in all major browsers?
             this._view.getHTMLCanvas().dispatchEvent(evt);
             break;
             */
            case "Enter": // ENTER
                // case 27: // ESC // problem to catch and used to cancel fullscreen mode
                // this._view.resetViewMode();
                // notify gui
                break;
            case "i": // i show debug log dialog
            case "d": // d show debug log dialog
                // Debug.toggleShowDebugConsole = !Debug.toggleShowDebugConsole;
                // Debug.activate(cDebug.toggleShowDebugConsole);
                break;
            case "/":  // / show properties hud
                this._sceneAPI.getStateViewer().toggle();
                break;
            default:
                break;
        }
    }

    public rotateCameraTarget(aRadX: number, aRadY: number) {
        super.rotateCameraTarget(aRadX, aRadY);
    }

    // handles an event on the canvas for the pointerlock example
    public onMovePointerLock(e: any) {
        let canvas = this._view.getHTMLCanvas();

        super.onMovePointerLock(e);

        super.rotateCameraTarget(e.dx / canvas.width * 360.0, -e.dy / canvas.height * 180.0);
    }

    public setFreeLook(aValue: boolean) {
        this._freeLook = aValue;
    }

    public setRepeat(aValue: boolean) {
        this._repeat = aValue;
    }

    public animated(aAnimatorObject: CameraAnimator, aTimeFactor: number) {
        let interpolator = aAnimatorObject.getInterpolator();

        if (!this._freeLook) {
            this.setCameraPositionAndTarget(
                interpolator.getPosition(),
                interpolator.getTarget()
            );
        }
        else {
            // get current view direction
            let camera = this.getCamera();
            let viewDir = camera.getCameraDirection();

            let target = vec3.create();
            vec3.add(target, interpolator.getPosition(), viewDir);

            this.setCameraPositionAndTarget(
                interpolator.getPosition(),
                target
            );
        }

        this._view.viewpointChanged();
    }

    protected _initCameraParams() {
        super._initCameraParams.call(this);

        this.openingAngle = 80.0;

        this._camera.setFieldOfView(degToRad(this.openingAngle));

        // this is for non logarithmic rendering, for logarithmic rendering the near plane is effectively zero
        this._camera.forceNearValue(0.1);
    }
}
